import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { uploadChatFile } from "../apis/chatApi";
import { FILE_SIZE_LIMIT, MESSAGE_SEND_ERROR, SCREENS } from "../constants/constants";
import { setMessengerOpenRoomId } from "../stores/messengerOpenRoomStore";
import { formatMessageTime } from "../utils/formatRoomTime";
import { formatDateLabel } from "../utils/messageMapping";
import { useChatWebSocketListeners } from "../websocket/useChatWebSocketListeners";
import { getChatSocket } from "../websocket/webSocketConnection";
import { sendMessageHistoryRequest, sendMessageSendRequest } from "../websocket/webSocketRequests";
import { WEBSOCKET_STATE } from "../websocket/webSocketState";
import { useMarkRoomRead } from "./useMarkRoomRead";
import { useRequestMessageHistory } from "./useRequestMessageHistory";

function revokeMessageBlobUrls(msg) {
  const urls = [];
  if (msg?.imageUrl) urls.push(msg.imageUrl);
  if (Array.isArray(msg?.imageUrls)) urls.push(...msg.imageUrls);
  urls.forEach((u) => {
    if (typeof u === "string" && u.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(u);
      } catch {
        /* ignore */
      }
    }
  });
}

/**
 * 채팅방 메시지/페이지네이션/전송 상태를 관리하는 커스텀 훅
 * - ChatPage에서 채팅방 화면 관련 state·ref·handler를 분리
 * @param {{ setSelectedRoom }} options — 선택 방 갱신 (읽음 영수증 등), rooms는 queryClient로 직접 관리
 */
export function useChatRoom(selectedRoom, screen, { setSelectedRoom, onForcedLeave }) {
  const [messages, setMessages] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  const selectedRoomIdRef = useRef(null);
  const historyLoadMoreRef = useRef(false);
  /** 재연결 직후 히스토리 ACK는 전체 교체 대신 메시지 병합 */
  const mergeReconnectRoomIdRef = useRef(null);
  selectedRoomIdRef.current = selectedRoom?.id ?? null;

  useEffect(() => {
    const open = screen === SCREENS.CHAT_ROOM && selectedRoom?.id != null ? selectedRoom.id : null;
    setMessengerOpenRoomId(open);
    return () => setMessengerOpenRoomId(null);
  }, [screen, selectedRoom?.id]);

  useChatWebSocketListeners({
    selectedRoomIdRef,
    setSelectedRoom,
    setCurrentRoomMessages: setMessages,
    setCurrentRoomNextCursor: setNextCursor,
    setCurrentRoomHasMore: setHasMore,
    historyLoadMoreRef,
    mergeReconnectRoomIdRef,
    setHistoryLoading,
    onForcedLeave,
  });

  useRequestMessageHistory(selectedRoom?.id, screen === SCREENS.CHAT_ROOM, {
    setMessages,
    setNextCursor,
    setHasMore,
    mergeReconnectRoomIdRef,
  });

  useMarkRoomRead(selectedRoom?.id, screen === SCREENS.CHAT_ROOM, messages);

  const handleLoadMore = useCallback(() => {
    const roomId = selectedRoom?.id;
    if (!roomId || !hasMore || historyLoading || historyLoadMoreRef.current) return;
    const socket = getChatSocket();
    if (!socket) {
      return;
    }
    historyLoadMoreRef.current = true;
    setHistoryLoading(true);
    sendMessageHistoryRequest(socket, roomId, nextCursor ?? undefined, 20);
  }, [selectedRoom?.id, hasMore, historyLoading, nextCursor]);

  /**
   * MESSAGE.SEND 단일 프레임: text + fileIds[] + content (re.disco.im 프로토콜과 동일)
   * @param {{ text?: string, files?: File[], content?: { contentId: string, contentType: string } }} payload
   */
  const handleSendMessage = useCallback(
    async (payload) => {
      const text = typeof payload === "string" ? payload : (payload?.text ?? "");
      const files = typeof payload === "string" ? [] : (payload?.files ?? []);
      const content = typeof payload === "string" ? undefined : (payload?.content ?? undefined);
      const trimmed = text.trim();
      const roomId = selectedRoom?.id;
      if (!roomId || (!trimmed && (!files || !files.length) && !content)) return;

      const nowIso = new Date().toISOString();
      const optimisticId = uuidv4();
      const imageFiles = files.filter(
        (f) => f.type?.startsWith("image/") && f.type !== "image/svg+xml"
      );
      const nonImageFiles = files.filter(
        (f) => !f.type?.startsWith("image/") || f.type === "image/svg+xml"
      );
      const imageUrls =
        imageFiles.length > 0 ? imageFiles.map((f) => URL.createObjectURL(f)) : null;
      const imageFileNames = imageFiles.length > 0 ? imageFiles.map((f) => f.name) : null;
      const fileAttachments =
        nonImageFiles.length > 0
          ? nonImageFiles.map((f) => ({
              fileName: f.name,
              fileExtension: f.name.includes(".") ? f.name.split(".").pop() : null,
              fileUrl: null,
            }))
          : null;
      const firstFile = fileAttachments?.[0];

      const optimistic = {
        id: optimisticId,
        roomId,
        /** ACK 후 id가 서버 messageId로 바뀌어도 리스트 key·동일 행 유지 */
        clientRequestId: optimisticId,
        body: trimmed,
        time: formatMessageTime(nowIso),
        type: "my",
        senderName: "",
        dateLabel: formatDateLabel(nowIso),
        imageUrl: imageUrls?.length === 1 ? imageUrls[0] : null,
        imageUrls,
        imageFileNames,
        fileAttachments,
        fileName: firstFile?.fileName ?? null,
        fileExtension: firstFile?.fileExtension ?? null,
        fileUrl: null,
        content: content ?? null,
        status: WEBSOCKET_STATE.PENDING,
        retryDraft: { text: trimmed, files, content },
      };
      setMessages((prev) => [...prev, optimistic]);
      const socket = getChatSocket();
      if (!socket) {
        setMessages((prev) =>
          prev.map((m) => (m.id === optimisticId ? { ...m, status: WEBSOCKET_STATE.FAILED } : m))
        );
        return;
      }

      // 개발 전용: 콘솔에서 localStorage.setItem("CHAT_SEND_DELAY_MS", "5000") 후 새로고침
      // if (process.env.NODE_ENV === "development") {
      //   try {
      //     const raw = window.localStorage.getItem("CHAT_SEND_DELAY_MS");
      //     if (raw != null && raw !== "") {
      //       const ms = Number(raw) || 3000;
      //       await new Promise((r) => setTimeout(r, ms));
      //     }
      //   } catch {
      //     /* ignore */
      //   }
      // }

      const oversized = files.find((f) => f.size > FILE_SIZE_LIMIT.PER_FILE);
      const totalSize = files.reduce((sum, f) => sum + f.size, 0);
      let failReason = null;
      if (oversized) failReason = MESSAGE_SEND_ERROR.FILE_TOO_LARGE;
      else if (totalSize > FILE_SIZE_LIMIT.TOTAL)
        failReason = MESSAGE_SEND_ERROR.FILE_TOO_LARGE_TOTAL;
      if (failReason) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === optimisticId ? { ...m, status: WEBSOCKET_STATE.FAILED, failReason } : m
          )
        );
        return;
      }

      let fileIds = [];
      if (files.length > 0) {
        try {
          fileIds = (await Promise.all(files.map((file) => uploadChatFile(file, roomId)))).map(
            (res) => res.file_id
          );
        } catch (err) {
          const failReason =
            err?.status === 413 ? MESSAGE_SEND_ERROR.FILE_TOO_LARGE_TOTAL : undefined;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === optimisticId ? { ...m, status: WEBSOCKET_STATE.FAILED, failReason } : m
            )
          );
          return;
        }
      }
      const sock = getChatSocket();

      if (!sock) {
        setMessages((prev) =>
          prev.map((m) => (m.id === optimisticId ? { ...m, status: WEBSOCKET_STATE.FAILED } : m))
        );
        return;
      }
      const sent = sendMessageSendRequest(
        sock,
        roomId,
        trimmed || undefined,
        fileIds.length ? fileIds : undefined,
        optimisticId,
        content
      );
      if (!sent) {
        setMessages((prev) =>
          prev.map((m) => (m.id === optimisticId ? { ...m, status: WEBSOCKET_STATE.FAILED } : m))
        );
      }
    },
    [selectedRoom?.id]
  );

  const handleRemoveFailedMessage = useCallback((msg) => {
    const key = msg?.clientRequestId ?? msg?.id;
    if (key == null) return;
    revokeMessageBlobUrls(msg);
    setMessages((prev) => prev.filter((m) => (m.clientRequestId ?? m.id) !== key));
  }, []);

  const handleRetryFailedMessage = useCallback(
    (msg) => {
      const key = msg?.clientRequestId ?? msg?.id;
      if (key == null) return;
      const draft = msg?.retryDraft;
      const nextDraft =
        draft && (String(draft.text ?? "").trim() || (draft.files?.length ?? 0) > 0)
          ? draft
          : { text: msg?.body ?? "", files: [] };
      if (!String(nextDraft.text ?? "").trim() && !(nextDraft.files?.length > 0)) return;
      revokeMessageBlobUrls(msg);
      setMessages((prev) => prev.filter((m) => (m.clientRequestId ?? m.id) !== key));
      handleSendMessage(nextDraft);
    },
    [handleSendMessage]
  );

  return {
    messages,
    hasMore,
    historyLoading,
    handleLoadMore,
    handleSendMessage,
    handleRetryFailedMessage,
    handleRemoveFailedMessage,
  };
}
